import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component, Input,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { buildProviders, Coerce } from '@kalidea/kaligraphi';

import {
    Observable,
    of
} from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import {
    AdresseDto,
    StatusDtoEnum
} from 'src/graphql/generated-types';

import { isEmpty } from 'src/app/utils/object.utils';

import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';

export const addressLines = {
    Ligne1: 'Apt. - Etage',
    Ligne2: 'Entrée - Immeuble',
    Ligne3: 'N°, Type de voie',
    Ligne4: 'BP - Lieu-dit',
};

const LINE_MAX_LENGTH = 38;

@Component({
    selector: 'app-shared-address-edit-component',
    templateUrl: './shared-address-edit.component.html',
    styleUrls: ['./shared-address-edit.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedAddressEditComponent),
    standalone: false
})
export class SharedAddressEditComponent extends CustomFormControlAccess implements OnInit {

    @Coerce('boolean')
    @Input()
    cityNotRequired = false;

    readonly addressLines = addressLines;

    form: UntypedFormGroup;

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly fb: UntypedFormBuilder) {
        super();
    }

    validate(c: AbstractControl): Observable<{}> {
        return of(this.form.valid ? null : {invalidAddress: true});
    }

    private updateForm(address: AdresseDto): void {
        const {
            Id,
            Ville,
            ...LignesAdresse
        } = address || {};

        this.form.patchValue({
            Id,
            Ville: {
                cityId: Ville?.Id || null,
                cityName: Ville?.Libelle || null,
                postalCode: Ville?.CodePostal || null,
            },
            ...LignesAdresse
        }, {emitEvent: false});
    }

    writeValue(address: AdresseDto): void {
        if (!isEmpty(address)) {
            this.updateForm(address);
        } else if (this.form.dirty) {
            this.form.reset();
        }
        super.writeValue(address);
    }

    private initSubscriptions(): void {
        this.watch$ = this.form.get('Ville').valueChanges.pipe(
            filter(() => !this.cityNotRequired),
            tap(({ cityName, cityId, postalCode }) => {
                const isCityValid = !!cityName && !!cityId && !!postalCode;

                Object.keys(addressLines).forEach(lineKey => {
                    const addressLineControl = this.form.get(lineKey);
                    addressLineControl[!!isCityValid ? 'enable' : 'disable']({ emitEvent: false });
                    if (!postalCode && !cityName) {
                        addressLineControl.patchValue(null);
                    }
                });

            })
        );

        this.watch$ = this.form.valueChanges.pipe(
            tap(address => {
                const {
                    Id,
                    Ville,
                    ...LignesAdresse
                } = address;

                const {
                    cityName,
                    cityId,
                    postalCode,
                } = Ville || {};

                if (isEmpty(Ville) && isEmpty(LignesAdresse)) {
                    super.notifyUpdate(null);
                } else {
                    const address = {
                        Ville: !!cityId
                            ? {
                                Id: cityId || null,
                                Libelle: cityName || null,
                                CodePostal: postalCode || null,
                            }
                            : null,
                        Id: Id || 0,
                        Status: !!Id ? StatusDtoEnum.Updated : StatusDtoEnum.Inserted,
                        ...LignesAdresse,
                    };
                    this.notifyUpdate(address);
                }
            })
        );
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            Id: [null],
            Ville: [null],
        });

        Object.keys(addressLines).forEach(lineKey => {
            this.form.addControl(lineKey, this.fb.control(
                null,
                Validators.maxLength(LINE_MAX_LENGTH)),
                { emitEvent: false }
            );
        });

        this.initSubscriptions();
    }
}
